import BaseService from '../../base/base_service';
import Device from '../../utils/device/device';
import IHttp2Service from './http2_service_interface';
import {isAbsolutUrl} from "../../../../utils/url-utils";
import {Check} from "@sparkware/app-utils";
import {ClientIpsHandler} from "./client-ips-handler";
import {CGP_ERROR_CODES} from "../../exceptions/cgp_error_codes";
import _ from '../../utils/underscore';
import {parseFetchResponse} from "../../../../common/utils/parse-fetch-response";

type ParameterValue = string | number | boolean | null | undefined;
export interface Http2ServiceParams {
    origin: string;
}

interface IFetchError {
    error_code: string;
    error_description?: string;
    error?: string;
    [key: string]: any;
}

interface IFetchOptions {
    method: string;
    headers?: HeadersInit;
    body?: any
    encodeBody?: (bodyData: any) => any;
    keepalive?:boolean;
}

export default class Http2Service extends BaseService implements IHttp2Service {
    /**
     * @static
     * @type {string}
     */
    static CLASS = 'Http2Service';

    private _origin: string = '';

    init(params: Http2ServiceParams) {
        super.init(params);
        this._origin = params?.origin || window.location.origin;

    }

    /**
     * Set the origin of the request from the configuration if not something else set.
     */
    public appendOrigin(url: string): string {
        if (isAbsolutUrl(url)) {
            return url;
        } else {
            return this._origin + url;
        }
    }


    private _clientIPsHandler: ClientIpsHandler;
    private get clientIPsHandler(): ClientIpsHandler {
        if(!this._clientIPsHandler) {
            this._clientIPsHandler = new ClientIpsHandler(this.serviceFactory);
        }
        return this._clientIPsHandler;
    }

    private _urlEncodeOneParam(name: string, value: ParameterValue | ParameterValue[]): string {
        if(Check.isArray(value)) {
            return value.filter(item => !Check.isNullOrUndefined(item))
                        .map(item => `${encodeURIComponent(name)}=${encodeURIComponent(item.toString())}`)
                        .join('&');
        } else {
            return `${encodeURIComponent(name)}=${encodeURIComponent(value?.toString() || '')}`
        }
    }

    private _urlEncodeUrlParams(params: object): string {
        return Object.keys(params)
            .map(propertyName =>  this._urlEncodeOneParam(propertyName, params[propertyName]))
            .join('&');
    }

    private _encodeXFormBodyParams(body?: object): string {
        body = {
            ...body,
            ...this._applicationParams()
        };

        return this._urlEncodeUrlParams(body);
    }


    private _parseFetchError(error: IFetchError): IFetchError {
        let error_code: string = CGP_ERROR_CODES.GENERAL_ERROR_SUPPORT.toString();
        if(error.error === CGP_ERROR_CODES.INVALID_GRANT) {
            error_code = CGP_ERROR_CODES.INVALID_GRANT
        }
        error = {
            error_code: error_code,
            ...error
        };

        if(error.error_description) {
            error = {
                ...error,
                ..._.safeJSON(error.error_description, {})
            }
        }

        return error;
    }


    private async _executeFetch<T = any>(url: string, options: IFetchOptions): Promise<T> {
        const response = await fetch(url, {
            method: options.method,
            body: options.encodeBody ? options.encodeBody(options.body) : options.body,
            headers: options.headers,
            keepalive:options.keepalive
        });


        if(response.status >= 400) {
            let error: IFetchError = {error_code: CGP_ERROR_CODES.GENERAL_ERROR_SUPPORT.toString()};
            try {
                error = await parseFetchResponse(response);
            } catch (err) {
                this.logger.error(`Fetch response.json() failed ${options.method} ${url}`, err);
            }

            error = this._parseFetchError(error);

            this._logFetchError(
                options.method,
                url, {
                    errorStatusCode: response.status,
                    errorStatusText: response.statusText,
                    ...error
                },
                options.body,
                options.headers);

            throw error;
        }

        this.clientIPsHandler.extractClientIpFromResponseHeaders(response.headers);

        if(response.status !== 204) { //204 means no content and this happens when the API doesn't return any data (a void operation on the backend side)
            return await parseFetchResponse(response);
        }

        return undefined;
    }


    /*
     private _responseError(errorResponse: IXHRErrorResponse, requestData: IRequestData) {
        let errorData = `url --> ${requestData.url}`;
        let errorMsg = '';
        // Is the error response is valid?
        if (_.isNull(errorResponse) || _.isUndefined(errorResponse)) {
            this.logger.silly('Error response is not defined server problem!');
            errorResponse = Http2Service.DEFAULT_ERROR_RESPONSE;
            this.logger.silly('Client http request error handling when server not support build the error => ', errorResponse);
        }

        let parsedResponse: any = {};
        // Is error response came with no data?
        if (!errorResponse.data) {
            errorMsg +='Response back with unhandled data response! and error code!';
            this.logger.silly(errorData, errorMsg);
            parsedResponse.error_code = CGP_ERROR_CODES.GENERAL_ERROR_SUPPORT.toString();
        } else {
            parsedResponse = _.safeJSON(errorResponse.data, {error_code: CGP_ERROR_CODES.GENERAL_ERROR_SUPPORT.toString()});
            if (parsedResponse.error && parsedResponse.error_description) {
                //this happens for token request where the response contains more error information inside error_description field
                let innerResponse = _.safeJSON(parsedResponse.error_description, null);
                if(innerResponse && innerResponse.error_code) { // we have error information inside error_description so we use that
                    parsedResponse = innerResponse;
                }

            }
        }

        // Is there no error code error response?
        if (!parsedResponse.error_code) {
            this.logger.silly('Status code is undefined, default behavior set the status to 500.');
            parsedResponse.error_code = CGP_ERROR_CODES.GENERAL_ERROR_SUPPORT.toString();
        }

        // print all possible information about request / response
        if (errorResponse.status) {
            errorData += ` status --> ${errorResponse.status}`;
        }
        if (errorResponse.statusText) {
            errorData += ` statusText --> ${errorResponse.statusText}`;
        }
        errorMsg = 'Response return with an error';
        let responseLog = {
            errorResponse: errorResponse,
            errorRequest: requestData
        };

        let authenticationData: any = responseLog.errorRequest?.params?.authenticationData;
        if(authenticationData) {
            try {
                authenticationData = JSON.parse(authenticationData);
                if(authenticationData?.credentials) {
                    authenticationData.credentials.password = '*';
                    responseLog.errorRequest.params.authenticationData = authenticationData;
                }
            }catch(err){}
        }

        this.logger.error(errorData, errorMsg, responseLog);

        return parsedResponse;
    }
     */

    /**
     * HTTP post using Content-Type: application/x-www-form-urlencoded
     */

    async post<T>(url: string, body?: object, headers?: HeadersInit): Promise<T> {
        headers = {
            'Content-Type': 'application/x-www-form-urlencoded',
            ...headers
        };

        url = this.appendOrigin(url);

        return await this._executeFetch(url, {
            method: 'POST',
            headers: headers,
            body: body,
            encodeBody: body => this._encodeXFormBodyParams(body)
        });
    }

    private _logFetchError(method: string, url: string, error: any, request?: any, headers?: HeadersInit) {
        request = {...request};
        let authenticationData: any = request.authenticationData;
        if(authenticationData) {
            try {
                authenticationData = JSON.parse(authenticationData);
                if(authenticationData?.credentials) {
                    authenticationData.credentials.password = '*';
                    request.authenticationData = authenticationData;
                }
            }catch(err){}
        }


        this.logger.error(`Http request failed: ${method} ${url}`, {
            error: error,
            request: request,
            headers: headers
        });
    }

    async postJSON<T=any>(url: string, options:{body?: Object, headers?: HeadersInit, keepalive?:boolean}): Promise<T>{
        url = this.appendOrigin(url);
        options.headers = {
            'Content-Type': 'application/json',
            ...options.headers
        }
        return await this._executeFetch<T>(url, {
            method: 'POST',
            headers: options.headers,
            body: JSON.stringify(options.body),
            keepalive:options.keepalive //see here when we need it : https://chromestatus.com/feature/4664843055398912
        });
    }


    /**
     * HTTP get request you can send custom params and headers.
     */
    async get<T>(url: string, params?: object, headers?: HeadersInit): Promise<T> {
        let fullURL = new URL(this.appendOrigin(url));
        if(params) {
            if(fullURL.search) {
                fullURL.search = fullURL.search + '&' + this._urlEncodeUrlParams(params);
            } else {
                fullURL.search = this._urlEncodeUrlParams(params);
            }
        }

        return await this._executeFetch<T>(fullURL.href, {
            method: 'GET',
            headers: headers
        });
    }


    private _getClientProperties() {
        const skinService = this.serviceFactory.skinService();
        return {
            version: skinService.getVersion(),
            brandName: skinService.getBrandName(),
            subBrandId: skinService.getSubBrandID(),
            brandId: skinService.getBrandID(),
            productPackageId: skinService.getProductPackageID(),
            screenWidth: Device.viewport.screenWidth,
            screenHeight: Device.viewport.screenHeight,
            language: this.serviceFactory.languageService().getISO3(),
            operatingSystem: Device.os.name
        };
    }



    /**
     * Get essential application params depend on request.
     */
    private _applicationParams() {
        const params: any = {};

        // Set the current application iso3 language.
        params.lang = this.serviceFactory.languageService().getISO3();

        // Get the token bearer authorization to set on the header.
        const getBearerToken = this.serviceFactory.oAuth2Service().getTokenAuthorizeHeader();

        // Is token bearer exist? if yes send it with the request.
        if (getBearerToken) {
            params.Authorization = getBearerToken;
        }
        params.clientRequestId = Device.generateGuid().replace(/-/g, '');
        params.clientProperties = JSON.stringify(this._getClientProperties());


        const fakeClientIp = this.serviceFactory.oAuth2Service().getFakeClientIP();
        if(fakeClientIp) {
            params.FakeClientIP = fakeClientIp;
        }

        const cmsSource = this.serviceFactory.integrationService().getCmsSourceIfValid();
        if(cmsSource) {
            params.CGP_CMSSource = cmsSource;
        }
        params.CGP_DomainOrigin = window.location.origin;

        const clientIPs = this.clientIPsHandler.beginNewRequest();
        if(clientIPs) {
            params.clientIPs = clientIPs;
        }


        return params;
    }
}
